import { useCallback } from 'react';
import * as THREE from 'three';

import { IBuildingProviderContext } from '../../../@types/ContextProviderRelated';
import { EHistoryAction } from '../../../hooks/useUndoRedo';
import { consolidateBuildingWalls } from '../../../hooks/useWallDragging';

import { IBoundaryEdge } from '../../../models/abstract/BoundaryEdge';
import { IWall } from '../../../models/abstract/Wall';
import FloorModel from '../../../models/standard/FloorModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../../models/standard/PhysicalBoundary';
import RoomModel from '../../../models/standard/RoomModel';
import SpaceModel, { ISpace } from '../../../models/standard/SpaceModel';
import StandardWallModel from '../../../models/standard/StandardWallModel';
import { cmToInternalRounded } from '../../../utils/conversionUtils';
import { collectBuildingEdgesForWallHelper, splitWallInBuildingHelper } from '../../../utils/multiroomUtils';
import { useAnnotations } from '../../AnnotationsProvider';

/* =========================================================================
   Math helpers
   ========================================================================= */

const EPS = 1e-6;
const DEG = Math.PI / 180;

const samePt = (a: THREE.Vector3, b: THREE.Vector3, eps = 1e-5) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

function intervalOverlap(a0: number, a1: number, b0: number, b1: number, eps = EPS): [number, number] | null {
  const lo = Math.max(Math.min(a0, a1), Math.min(b0, b1));
  const hi = Math.min(Math.max(a0, a1), Math.max(b0, b1));
  return hi - lo > eps ? [lo, hi] : null;
}

const getPointSafe = (w: any, key: 'startPoint' | 'endPoint'): THREE.Vector3 | undefined => {
  const p = w?.[key];
  if (!p) return undefined;
  if (p instanceof THREE.Vector3) return p as THREE.Vector3;
  if (typeof p.x === 'number' && typeof p.y === 'number') {
    return new THREE.Vector3(p.x, p.y, p.z ?? 0);
  }
  return undefined;
};

function ensureClockwiseEdges(edges: IBoundaryEdge[]): IBoundaryEdge[] {
  if (!edges || edges.length < 3) return edges;

  const getGeom = (ed: IBoundaryEdge) => {
    const wStart = getPointSafe(ed.wall, 'startPoint')!;
    const wEnd = getPointSafe(ed.wall, 'endPoint')!;
    const pt0 = wStart.clone().lerp(wEnd, ed.t0);
    const pt1 = wStart.clone().lerp(wEnd, ed.t1);
    return ed.reversed ? { s: pt1, e: pt0 } : { s: pt0, e: pt1 };
  };

  const out: { edge: IBoundaryEdge; s: THREE.Vector3; e: THREE.Vector3 }[] = [];
  const used = new Array(edges.length).fill(false);

  const g0 = getGeom(edges[0]);
  used[0] = true;
  out.push({ edge: edges[0], s: g0.s, e: g0.e });
  let lastEnd = g0.e;

  for (let count = 1; count < edges.length; count++) {
    let found = false;
    for (let i = 1; i < edges.length; i++) {
      if (used[i]) continue;
      const g = getGeom(edges[i]);
      if (samePt(lastEnd, g.s)) {
        out.push({ edge: edges[i], s: g.s, e: g.e });
        lastEnd = g.e;
        used[i] = true;
        found = true;
        break;
      } else if (samePt(lastEnd, g.e)) {
        const flippedEdge = new BoundaryEdge(edges[i].wall, edges[i].t0, edges[i].t1, edges[i].side, !edges[i].reversed);
        out.push({ edge: flippedEdge, s: g.e, e: g.s });
        lastEnd = g.s;
        used[i] = true;
        found = true;
        break;
      }
    }
    if (!found) {
      const firstUnused = used.findIndex((u) => !u);
      if (firstUnused !== -1) {
        const g = getGeom(edges[firstUnused]);
        out.push({ edge: edges[firstUnused], s: g.s, e: g.e });
        lastEnd = g.e;
        used[firstUnused] = true;
      }
    }
  }

  const pts: THREE.Vector3[] = out.map((o) => o.s);
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    area += (p2.x - p1.x) * (p2.y + p1.y);
  }
  if (area < 0) {
    return out.map((o) => new BoundaryEdge(o.edge.wall, o.edge.t0, o.edge.t1, o.edge.side, !o.edge.reversed)).reverse();
  }

  return out.map((o) => o.edge);
}

function snapPointToWallIfOnSegment(building: IBuildingProviderContext, pt: THREE.Vector3, tolSnap = 0.01): THREE.Vector3 {
  let snapped: THREE.Vector3 | null = null;
  let bestDist = tolSnap;

  for (const w of building.walls ?? []) {
    const A = w.startPoint;
    const B = w.endPoint;

    const AB = B.clone().sub(A);
    const len2 = AB.lengthSq();
    if (len2 < EPS) continue;

    const t = Math.max(0, Math.min(1, pt.clone().sub(A).dot(AB) / len2));
    const proj = A.clone().addScaledVector(AB, t);

    const dist = proj.distanceTo(pt);
    if (dist <= bestDist) {
      snapped = proj;
      bestDist = dist;
    }
  }

  return snapped ? snapped : pt;
}

function cross2D(a: THREE.Vector3, b: THREE.Vector3): number {
  return a.x * b.y - a.y * b.x;
}

function intersectSegments(A: THREE.Vector3, B: THREE.Vector3, C: THREE.Vector3, D: THREE.Vector3): { u: number; v: number; point: THREE.Vector3 } | null {
  const r = B.clone().sub(A);
  const s = D.clone().sub(C);
  const rxs = cross2D(r, s);
  const cma = C.clone().sub(A);
  const cmaxs = cross2D(cma, s);
  const cmaxr = cross2D(cma, r);
  if (Math.abs(rxs) < EPS) return null;
  const u = cmaxs / rxs;
  const v = cmaxr / rxs;
  if (u < -EPS || u > 1 + EPS || v < -EPS || v > 1 + EPS) return null;
  const point = A.clone().addScaledVector(r, u);
  return { u, v, point };
}

function wallsOverlap(w1: IWall, w2: IWall): { t1Start: number; t1End: number; t2Start: number; t2End: number } | null {
  const p1 = w1.startPoint,
    p2 = w1.endPoint;
  const p3 = w2.startPoint,
    p4 = w2.endPoint;

  const v1 = p2.clone().sub(p1);
  const v2 = p4.clone().sub(p3);
  const len1 = v1.length();
  const len2 = v2.length();
  if (len1 < EPS || len2 < EPS) return null;

  const d1 = v1.clone().divideScalar(len1);
  const d2 = v2.clone().divideScalar(len2);

  const cross = Math.abs(d1.x * d2.y - d1.y * d2.x);
  if (cross > 2e-3) return null;

  const perpDist = Math.abs((p3.x - p1.x) * d1.y - (p3.y - p1.y) * d1.x);
  const PERP_TOL = Math.max(5e-4, 0.0005 * Math.max(len1, len2));

  // Im Center-Line System MÜSSEN die Linien exakt übereinander liegen (Collinear)
  if (perpDist > PERP_TOL) return null;

  const t3 = p3.clone().sub(p1).dot(d1) / len1;
  const t4 = p4.clone().sub(p1).dot(d1) / len1;

  const ov = intervalOverlap(0, 1, t3, t4, EPS);
  if (!ov) return null;
  const [ovStart, ovEnd] = ov;

  const mapW1toW2 = (t: number) => {
    const pt = p1.clone().addScaledVector(d1, t * len1);
    return pt.clone().sub(p3).dot(d2) / len2;
  };

  let sStart = mapW1toW2(ovStart);
  let sEnd = mapW1toW2(ovEnd);

  sStart = Math.max(0, Math.min(1, sStart));
  sEnd = Math.max(0, Math.min(1, sEnd));
  return { t1Start: ovStart, t1End: ovEnd, t2Start: Math.min(sStart, sEnd), t2End: Math.max(sStart, sEnd) };
}

function isParallel(u: THREE.Vector3, v: THREE.Vector3, maxAngleDeg = 7) {
  const a = u.clone().normalize(),
    b = v.clone().normalize();
  return Math.abs(a.dot(b)) >= Math.cos(maxAngleDeg * DEG);
}

function signedGapToLine(A: THREE.Vector3, B: THREE.Vector3, P: THREE.Vector3) {
  const AB = B.clone().sub(A);
  const n = new THREE.Vector3(-AB.y, AB.x, 0);
  const denom = Math.max(EPS, AB.length());
  return P.clone().sub(A).dot(n) / denom;
}

type EdgeRef = { roomIdx: number; spaceIdx: number; boundaryIdx: number; edgeIdx: number };

function collectBuildingEdgesForWall(building: IBuildingProviderContext, wallUuid: string): Array<{ ref: EdgeRef; edge: IBoundaryEdge }> {
  const out: Array<{ ref: EdgeRef; edge: IBoundaryEdge }> = [];
  (building.rooms ?? []).forEach((room, rI) => {
    (room.spaces ?? []).forEach((space: ISpace, sI) => {
      (space.boundaries ?? []).forEach((b, bI) => {
        if (!(b instanceof PhysicalBoundaryModel)) return;
        (b.edges ?? []).forEach((e, eI) => {
          if (e?.wall?.uuid === wallUuid) {
            out.push({ ref: { roomIdx: rI, spaceIdx: sI, boundaryIdx: bI, edgeIdx: eI }, edge: e });
          }
        });
      });
    });
  });
  return out;
}

function projectParam(wall: IWall, P: any) {
  const a = wall.startPoint;
  const b = wall.endPoint;
  const pVec = P instanceof THREE.Vector3 ? P : new THREE.Vector3(P.x, P.y, P.z || 0);
  const v = b.clone().sub(a);
  const len = Math.max(EPS, v.length());
  const d = v.clone().divideScalar(len);
  return pVec.clone().sub(a).dot(d) / len;
}

function computeDoorHostSegment(host: IWall, doorIndex: number) {
  if (!host || !Array.isArray((host as any).doors) || (host as any).doors.length === 0) return { t0: 0, t1: 1 };
  const doors = (host as any).doors;
  if (doors.length === 1) return { t0: 0, t1: 1 };

  const out = [];
  for (let i = 0; i < doors.length; i++) {
    const d = doors[i];
    const tVal = d.t ?? projectParam(host, d.position || d.center);
    if (Number.isFinite(tVal)) out.push({ origIndex: i, t: Math.max(0, Math.min(1, tVal!)) });
  }

  if (!out.length) return { t0: 0, t1: 1 };

  const sorted = out.sort((a, b) => a.t - b.t);
  const selected = sorted.find((d) => d.origIndex === doorIndex) ?? sorted[0];
  const idx = sorted.indexOf(selected);
  const n = sorted.length;

  if (idx === 0) return { t0: 0, t1: 0.5 * (selected.t + sorted[1].t) };
  if (idx === n - 1) return { t0: 0.5 * (sorted[n - 2].t + selected.t), t1: 1 };
  return { t0: 0.5 * (sorted[idx - 1].t + selected.t), t1: 0.5 * (selected.t + sorted[idx + 1].t) };
}

function computeDoorParamsWithIndices(host: any): Array<{ origIndex: number; t: number }> {
  if (!host || !Array.isArray((host as any).doors) || (host as any).doors.length === 0) return [];
  const out: Array<{ origIndex: number; t: number }> = [];
  for (let i = 0; i < (host as any).doors.length; i++) {
    const door = (host as any).doors[i];
    let tVal: number | undefined = undefined;

    if (typeof door === 'object' && door !== null) {
      if (typeof door.t === 'number') {
        tVal = door.t;
      } else {
        const pos = door.position || door.center || door.point;
        if (pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
          const pVec = new THREE.Vector3(pos.x, pos.y, pos.z || 0);
          tVal = projectParam(host, pVec);
        } else if (door.startPoint && typeof door.startPoint.x === 'number' && door.endPoint && typeof door.endPoint.x === 'number') {
          const s = new THREE.Vector3(door.startPoint.x, door.startPoint.y, door.startPoint.z || 0);
          const e = new THREE.Vector3(door.endPoint.x, door.endPoint.y, door.endPoint.z || 0);
          const mid = s.add(e).multiplyScalar(0.5);
          tVal = projectParam(host, mid);
        }
      }
    }

    if (Number.isFinite(tVal)) {
      const clamped = Math.max(0, Math.min(1, tVal!));
      out.push({ origIndex: i, t: clamped });
    }
  }
  return out;
}

/* =========================================================================
   Hook
   ========================================================================= */

export interface IMultiRoomActions {
  addAdjacentRoomWallsFromDoor: (wallUuid: string, doorIndex: number, opts?: { allowShareOuter?: boolean }) => void;
  connectTwoRooms: (wallIndexA: number, doorIndexA: number, wallIndexB: number, doorIndexB: number) => void;
  connectRoomViaDoor: (movingWallIdx: number, movingDoorIdx: number, targetWallIdx: number, targetDoorIdx: number) => void;
}

export const useMultiRoomActions = (setBuilding: React.Dispatch<React.SetStateAction<IBuildingProviderContext>>): IMultiRoomActions => {
  const { updateCameraPositionSprites } = useAnnotations();

  const cloneWithProto = <T>(obj: T): T => Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj);

  const getOppositeSide = (wallUuid: string, bld: any): 'A' | 'B' => {
    for (const r of bld.rooms ?? []) {
      for (const s of r.spaces ?? []) {
        for (const b of s.boundaries ?? []) {
          if (b instanceof PhysicalBoundaryModel) {
            const existingEdge = b.edges.find((e: any) => e.wall.uuid === wallUuid);
            if (existingEdge && existingEdge.side) return existingEdge.side === 'A' ? 'B' : 'A';
          }
        }
      }
    }
    return 'B'; // Fallback
  };

  const addAdjacentRoomWallsFromDoor = useCallback(
    (wallUuid: string, doorIndex: number, opts?: { allowShareOuter?: boolean }) => {
      setBuilding((prev) => {
        const building = prev;
        const wallIndex = building.walls.findIndex((w) => w.uuid === wallUuid);
        if (wallIndex === -1) return prev;
        const host = building.walls[wallIndex];
        if (!(host instanceof StandardWallModel)) return prev;

        const A = host.startPoint.clone();
        const B = host.endPoint.clone();
        const AB = B.clone().sub(A);
        const L = AB.length();
        if (L < 1e-6) return prev;

        const tDir = AB.clone().normalize();
        const nLeft = new THREE.Vector3(-tDir.y, tDir.x, 0).normalize();

        const { t0, t1 } = computeDoorHostSegment(host, doorIndex);

        let activeT0 = t0;
        let activeT1 = t1;

        const hostDirNorm = AB.clone().divideScalar(L);
        const nLeftForHost = new THREE.Vector3(-hostDirNorm.y, hostDirNorm.x, 0).normalize();
        const intersections: Array<{ wall: IWall; t: number }> = [];
        for (const ew of building.walls ?? []) {
          if (!(ew instanceof StandardWallModel) || ew.uuid === host.uuid) continue;
          const Vw = ew.endPoint.clone().sub(ew.startPoint);
          if (!isParallel(Vw.clone().normalize(), nLeftForHost, 10)) continue;
          const inter = intersectSegments(host.startPoint, host.endPoint, ew.startPoint, ew.endPoint);
          if (inter && inter.u > activeT0 + EPS && inter.u < activeT1 - EPS) intersections.push({ wall: ew, t: inter.u });
        }
        if (intersections.length > 0) {
          const doors = computeDoorParamsWithIndices(host);
          const selT = doors.find((d) => d.origIndex === doorIndex)?.t ?? 0.5;
          const sorted = intersections.sort((a, b) => Math.abs(a.t - selT) - Math.abs(b.t - selT));
          if (selT < sorted[0].t) activeT1 = sorted[0].t;
          else activeT0 = sorted[0].t;
        }

        const pointAt = (t: number) => host.startPoint.clone().lerp(host.endPoint, t);
        const p0 = pointAt(activeT0);
        const p1 = pointAt(activeT1);
        p0.copy(snapPointToWallIfOnSegment(building, p0, 0.1));
        p1.copy(snapPointToWallIfOnSegment(building, p1, 0.1));

        const rawDepth = p0.distanceTo(p1);
        const finalDepth = rawDepth > 1e-3 ? rawDepth : 1.0;

        const p0out = p0.clone().addScaledVector(nLeft, finalDepth);
        const p1out = p1.clone().addScaledVector(nLeft, finalDepth);
        p0out.copy(snapPointToWallIfOnSegment(building, p0out, 0.1));
        p1out.copy(snapPointToWallIfOnSegment(building, p1out, 0.1));

        const depthParam = (host as any).depth ?? 0;
        const attrParam = (host as any).attributes ? [...(host as any).attributes] : [];
        const wA = new StandardWallModel(p0.clone(), p0out.clone(), depthParam, [], [], [], attrParam, THREE.MathUtils.generateUUID());
        const wOut = new StandardWallModel(p0out.clone(), p1out.clone(), depthParam, [], [], [], attrParam, THREE.MathUtils.generateUUID());
        const wB = new StandardWallModel(p1out.clone(), p1.clone(), depthParam, [], [], [], attrParam, THREE.MathUtils.generateUUID());

        if (typeof wA.updateWallLengths === 'function') wA.updateWallLengths();
        if (typeof wOut.updateWallLengths === 'function') wOut.updateWallLengths();
        if (typeof wB.updateWallLengths === 'function') wB.updateWallLengths();

        const hostSide = getOppositeSide(host.uuid, building);

        const rawEdges = [
          new BoundaryEdge(host, activeT0, activeT1, hostSide, true),
          new BoundaryEdge(wA, 0, 1, 'A', false),
          new BoundaryEdge(wOut, 0, 1, 'A', false),
          new BoundaryEdge(wB, 0, 1, 'A', false),
        ];

        const boundary = new PhysicalBoundaryModel([host, wA, wOut, wB], ensureClockwiseEdges(rawEdges));
        const space = new SpaceModel(new FloorModel(), [], [], [], [boundary], [host, wA, wOut, wB]);
        const newRoom = new RoomModel('', [space], []);
        newRoom.setUUID(THREE.MathUtils.generateUUID());

        let nextBuilding = {
          ...building,
          walls: [...building.walls, wA, wOut, wB],
          rooms: [...(building.rooms || []), newRoom],
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ROOM_CREATED,
        };

        const cuts = [activeT0, activeT1].filter((t) => t > 1e-4 && t < 1 - 1e-4);
        if (cuts.length > 0) {
          nextBuilding = splitWallInBuildingHelper(nextBuilding, host.uuid, cuts);
        }

        let finalBuilding = nextBuilding;
        const addedRoom = finalBuilding.rooms.find((r) => r.uuid === newRoom.uuid);

        if (addedRoom && opts?.allowShareOuter !== false) {
          const roomAWallsUuids = new Set<string>();
          addedRoom.spaces.forEach((s) =>
            s.boundaries.forEach((b) => {
              if (b instanceof PhysicalBoundaryModel) b.edges.forEach((e) => roomAWallsUuids.add(e.wall.uuid));
            }),
          );

          const otherWalls = finalBuilding.walls.filter((w) => !roomAWallsUuids.has(w.uuid) && w.uuid !== host.uuid);

          let buildingChanged = false;
          const newWallsToAdd: IWall[] = [];
          const wallsToRemove = new Set<string>();

          addedRoom.spaces.forEach((spaceRef) => {
            spaceRef.boundaries.forEach((b) => {
              if (!(b instanceof PhysicalBoundaryModel)) return;

              const newEdges: any[] = [];

              b.edges.forEach((edge) => {
                const fw = edge.wall;

                if (fw.uuid === host.uuid) {
                  newEdges.push(edge);
                  return;
                }

                let bestOverlap: any = null;
                let bestOtherWall: any = null;

                for (const wOther of otherWalls) {
                  const ov = wallsOverlap(fw, wOther);
                  if (ov && (ov.t1End - ov.t1Start) * fw.startPoint.distanceTo(fw.endPoint) > 0.05) {
                    bestOverlap = ov;
                    bestOtherWall = wOther;
                    break;
                  }
                }

                if (bestOverlap && bestOtherWall) {
                  buildingChanged = true;
                  wallsToRemove.add(fw.uuid);

                  const fwLen = fw.startPoint.distanceTo(fw.endPoint);
                  const dirFw = fw.endPoint.clone().sub(fw.startPoint).normalize();
                  const piecesForEdge: any[] = [];

                  if (bestOverlap.t1Start > 1e-3) {
                    const pEnd = fw.startPoint.clone().addScaledVector(dirFw, bestOverlap.t1Start * fwLen);
                    const part1 = new StandardWallModel(
                      fw.startPoint.clone(),
                      pEnd,
                      (fw as any).depth,
                      [],
                      [],
                      [],
                      (fw as any).attributes ? [...(fw as any).attributes] : [],
                      THREE.MathUtils.generateUUID(),
                    );
                    if (typeof part1.updateWallLengths === 'function') part1.updateWallLengths();
                    newWallsToAdd.push(part1);
                    piecesForEdge.push(new BoundaryEdge(part1, 0, 1, edge.side, edge.reversed));
                  }

                  const newSide = getOppositeSide(bestOtherWall.uuid, finalBuilding);
                  const dirOther = bestOtherWall.endPoint.clone().sub(bestOtherWall.startPoint);
                  const newReversed = dirFw.dot(dirOther) < 0 ? !edge.reversed : edge.reversed;

                  piecesForEdge.push(new BoundaryEdge(bestOtherWall, bestOverlap.t2Start, bestOverlap.t2End, newSide, newReversed));

                  if (bestOverlap.t1End < 1 - 1e-3) {
                    const pStart = fw.startPoint.clone().addScaledVector(dirFw, bestOverlap.t1End * fwLen);
                    const part2 = new StandardWallModel(
                      pStart,
                      fw.endPoint.clone(),
                      (fw as any).depth,
                      [],
                      [],
                      [],
                      (fw as any).attributes ? [...(fw as any).attributes] : [],
                      THREE.MathUtils.generateUUID(),
                    );
                    if (typeof part2.updateWallLengths === 'function') part2.updateWallLengths();
                    newWallsToAdd.push(part2);
                    piecesForEdge.push(new BoundaryEdge(part2, 0, 1, edge.side, edge.reversed));
                  }

                  if (edge.reversed) {
                    piecesForEdge.reverse();
                  }

                  newEdges.push(...piecesForEdge);
                } else {
                  newEdges.push(edge);
                }
              });

              if (buildingChanged) {
                b.setEdges(ensureClockwiseEdges(newEdges));
              }
            });

            if (buildingChanged && spaceRef.walls) {
              spaceRef.walls = spaceRef.walls.filter((w) => !wallsToRemove.has(w.uuid));
              spaceRef.walls.push(...newWallsToAdd);
            }
          });

          if (buildingChanged) {
            const cleanedGlobalWalls = finalBuilding.walls.filter((w) => !wallsToRemove.has(w.uuid));
            cleanedGlobalWalls.push(...newWallsToAdd);

            finalBuilding = {
              ...finalBuilding,
              rooms: finalBuilding.rooms.map((r) => (r.uuid === newRoom.uuid ? addedRoom : r)),
              walls: cleanedGlobalWalls,
            };
          }
        }

        return finalBuilding;
      });
    },
    [setBuilding],
  );
  
  const connectRoomViaDoor = useCallback(
    (movingWallIdx: number, movingDoorIdx: number, targetWallIdx: number, targetDoorIdx: number) => {
      setBuilding((prev) => {
        const building = prev;
        const wallA = building.walls[movingWallIdx];
        const wallB = building.walls[targetWallIdx];

        if (!wallA || !wallB || !(wallA instanceof StandardWallModel) || !(wallB instanceof StandardWallModel)) return prev;

        const refsA = collectBuildingEdgesForWallHelper(building, wallA.uuid);
        if (refsA.length === 0) return prev;
        const ownerA = refsA[0].ref;
        const roomA = building.rooms[ownerA.roomIdx];
        if (!roomA) return prev;
        const movedSpaceUuids = new Set((roomA.spaces ?? []).map((space: any) => space?.uuid).filter(Boolean));
        const getOppositeSide = (wallUuid: string, bld: any): 'A' | 'B' => {
          for (const r of bld.rooms) {
            for (const s of r.spaces) {
              for (const b of s.boundaries) {
                if (b instanceof PhysicalBoundaryModel) {
                  const existingEdge = b.edges.find((e: any) => e.wall.uuid === wallUuid);
                  if (existingEdge && existingEdge.side) {
                    return existingEdge.side === 'A' ? 'B' : 'A';
                  }
                }
              }
            }
          }
          return 'B';
        };

        const getDoorT = (w: IWall, dIdx: number) => {
          const doors = computeDoorParamsWithIndices(w);
          return doors.find((d) => d.origIndex === dIdx)?.t ?? 0.5;
        };

        const tA = getDoorT(wallA, movingDoorIdx);
        const tB = getDoorT(wallB, targetDoorIdx);

        const doorAnchorA = wallA.startPoint.clone().lerp(wallA.endPoint, tA);
        const doorAnchorB = wallB.startPoint.clone().lerp(wallB.endPoint, tB);

        const dirA = wallA.endPoint.clone().sub(wallA.startPoint).normalize();
        const dirB = wallB.endPoint.clone().sub(wallB.startPoint).normalize();

        const getApproxCenter = (space: ISpace, wallsArr: IWall[]) => {
          let sumX = 0,
            sumY = 0,
            count = 0;
          space.boundaries.forEach((b) => {
            if (b instanceof PhysicalBoundaryModel) {
              b.edges.forEach((e) => {
                const w = wallsArr.find((wa) => wa.uuid === e.wall.uuid) || e.wall;
                sumX += w.startPoint.x + w.endPoint.x;
                sumY += w.startPoint.y + w.endPoint.y;
                count += 2;
              });
            }
          });
          return new THREE.Vector3(sumX / (count || 1), sumY / (count || 1), 0);
        };

        const centerALocal = getApproxCenter(roomA.spaces[0], building.walls);
        const refsB = collectBuildingEdgesForWallHelper(building, wallB.uuid);
        const roomB = building.rooms[refsB[0].ref.roomIdx];
        const centerBGlobal = getApproxCenter(roomB.spaces[0], building.walls);

        let angle = Math.atan2(dirB.y, dirB.x) - Math.atan2(dirA.y, dirA.x);

        const testTransform = (p: THREE.Vector3, testAngle: number) => {
          const v = p.clone().sub(doorAnchorA);
          const rotated = new THREE.Vector3(v.x * Math.cos(testAngle) - v.y * Math.sin(testAngle), v.x * Math.sin(testAngle) + v.y * Math.cos(testAngle), 0);
          return rotated.add(doorAnchorB);
        };

        const centerAGlobalTest = testTransform(centerALocal, angle);
        const dirBEnd = doorAnchorB.clone().add(dirB);

        if (cross2D(doorAnchorB, dirBEnd, centerAGlobalTest) * cross2D(doorAnchorB, dirBEnd, centerBGlobal) > 0) {
          angle += Math.PI;
        }

        const bStart = wallB.startPoint;
        const bEnd = wallB.endPoint;
        const bLen = bStart.distanceTo(bEnd);

        const rawTransAStart = testTransform(wallA.startPoint, angle);
        const rawTransAEnd = testTransform(wallA.endPoint, angle);

        const rawA0 = rawTransAStart.clone().sub(bStart).dot(dirB);
        const rawA1 = rawTransAEnd.clone().sub(bStart).dot(dirB);

        const maxDepth = Math.max((wallA as any).depth ?? 0, (wallB as any).depth ?? 0);
        const SNAP_TOLERANCE = maxDepth + cmToInternalRounded(5);

        let rigidCorrection = new THREE.Vector3(0, 0, 0);
        const corrections = [-rawA0, bLen - rawA0, -rawA1, bLen - rawA1];

        let bestCorrection = 0;
        let minAbs = Infinity;

        for (const c of corrections) {
          if (Math.abs(c) < SNAP_TOLERANCE && Math.abs(c) < minAbs) {
            minAbs = Math.abs(c);
            bestCorrection = c;
          }
        }

        if (minAbs !== Infinity) {
          rigidCorrection = dirB.clone().multiplyScalar(bestCorrection);
        }

        const transformPoint = (p: THREE.Vector3) => {
          const baseP = testTransform(p, angle);
          p.copy(baseP.add(rigidCorrection));
        };

        const wallsInRoomA = new Set<string>();
        roomA.spaces.forEach((s) => {
          s.walls.forEach((w) => wallsInRoomA.add(w.uuid));
          s.boundaries.forEach((b) => {
            if (b instanceof PhysicalBoundaryModel) b.walls.forEach((w) => wallsInRoomA.add(w.uuid));
          });
        });

        const safeClone = <T,>(obj: T): T => {
          if (!obj || typeof obj !== 'object') return obj;
        
          const cloned: any = Object.assign(Object.create(Object.getPrototypeOf(obj as any)), obj);
        
          ['position', 'center', 'point', 'startPoint', 'endPoint'].forEach((key) => {
            const value = cloned[key];
        
            if (value instanceof THREE.Vector3) {
              cloned[key] = value.clone();
            } else if (value && typeof value.x === 'number' && typeof value.y === 'number') {
              cloned[key] = new THREE.Vector3(value.x, value.y, value.z ?? 0);
            }
          });
        
          if (cloned.rotation instanceof THREE.Euler) {
            cloned.rotation = cloned.rotation.clone();
          }
        
          return cloned as T;
        };       
         const transformWallAsset = <T>(item: T): T => {
          const cloned: any = safeClone(item);

          if (cloned?.position) {
            transformPoint(cloned.position);
          }

          if (cloned?.center) {
            transformPoint(cloned.center);
          }

          if (cloned?.point) {
            transformPoint(cloned.point);
          }

          if (cloned?.startPoint) {
            transformPoint(cloned.startPoint);
          }

          if (cloned?.endPoint) {
            transformPoint(cloned.endPoint);
          }

          if (typeof cloned?.angle === 'number') {
            cloned.angle = (cloned.angle + angle) % (2 * Math.PI);
          }

          if (typeof cloned?.rotation === 'number') {
            cloned.rotation = (cloned.rotation + angle) % (2 * Math.PI);
          } else if (cloned?.rotation instanceof THREE.Euler) {
            cloned.rotation.z = (cloned.rotation.z + angle) % (2 * Math.PI);
          }

          return cloned as T;
        };

        const transformStandardWallGeometry = (wall: any) => {
          transformPoint(wall.startPoint);
          transformPoint(wall.endPoint);
        
          if (wall.outerStartPoint) transformPoint(wall.outerStartPoint);
          if (wall.outerEndPoint) transformPoint(wall.outerEndPoint);
          if (wall.outerStartPoint2) transformPoint(wall.outerStartPoint2);
          if (wall.outerEndPoint2) transformPoint(wall.outerEndPoint2);
        
          if (typeof wall.updateWallLengths === 'function') {
            wall.updateWallLengths();
          }
        };
        const newWalls = building.walls.map((w) => {
          if (wallsInRoomA.has(w.uuid)) {
            const clonedDoors = (w as any).doors?.map((d: any) => safeClone(d)) || [];
            if (w.uuid === wallA.uuid) {
              clonedDoors.splice(movingDoorIdx, 1);
            }
        
            const cloned = new StandardWallModel(
              w.startPoint.clone(),
              w.endPoint.clone(),
              (w as any).depth,
              ((w as any).windows ?? []).map(transformWallAsset),
              clonedDoors.map(transformWallAsset),
              [],
              (w as any).attributes ? [...(w as any).attributes] : [],
              w.uuid,
              ((w as any).passages ?? []).map(transformWallAsset),
            );
        
            if ((w as any).sides) {
              cloned.sides.A.radiators = ((w as any).sides.A?.radiators ?? []).map(transformWallAsset);
              cloned.sides.B.radiators = ((w as any).sides.B?.radiators ?? []).map(transformWallAsset);
            }
        
            transformStandardWallGeometry(cloned);
            return cloned;
          }
        
          return w;
        });

        const transformItem = (item: any) => {
          if (!item || !item.position) return item;
          const cloned = safeClone(item);
          transformPoint(cloned.position);
          if (typeof cloned.angle === 'number') cloned.angle = (cloned.angle + angle) % (2 * Math.PI);
          if (cloned.rotation) {
            if (typeof cloned.rotation === 'number') cloned.rotation = (cloned.rotation + angle) % (2 * Math.PI);
            else if (cloned.rotation instanceof THREE.Euler) cloned.rotation.z = (cloned.rotation.z + angle) % (2 * Math.PI);
          }
          return cloned;
        };

        let tempBuilding = { ...building, walls: newWalls };
        const movedRoomA = tempBuilding.rooms.find((r) => r.uuid === roomA.uuid);
        if (!movedRoomA) return prev;

        movedRoomA.spaces.forEach((s) => {
          s.walls = s.walls.map((w) => newWalls.find((fw) => fw.uuid === w.uuid) || w);
          s.products = s.products?.map(transformItem) || [];
          s.elements = s.elements?.map(transformItem) || [];
          s.models2D = s.models2D?.map(transformItem) || [];
          s.boundaries.forEach((b) => {
            if (b instanceof PhysicalBoundaryModel) {
              b.walls = b.walls.map((w) => newWalls.find((fw) => fw.uuid === w.uuid) || w);
              b.edges.forEach((e) => {
                e.wall = newWalls.find((fw) => fw.uuid === e.wall.uuid) || e.wall;
              });
            }
          });
        });

        updateCameraPositionSprites((prevSprites) =>
          prevSprites.map((sprite: any) => {
            if (!sprite?.spaceUuid || !movedSpaceUuids.has(sprite.spaceUuid)) {
              return sprite;
            }

            const cloned = safeClone(sprite);

            if (cloned.position) {
              transformPoint(cloned.position);
            }

            if (typeof cloned.angle === 'number') {
              cloned.angle = (cloned.angle + angle) % (2 * Math.PI);
            }

            if (typeof cloned.rotation === 'number') {
              cloned.rotation = (cloned.rotation + angle) % (2 * Math.PI);
            } else if (cloned.rotation instanceof THREE.Euler) {
              cloned.rotation.z = (cloned.rotation.z + angle) % (2 * Math.PI);
            }

            return cloned;
          }),
        );
        const wallsToSplit = new Map<string, Set<number>>();
        const SLIVER_TOLERANCE = cmToInternalRounded(2);

        const roomAWallsArray = newWalls.filter((w) => wallsInRoomA.has(w.uuid));
        const otherWallsArray = newWalls.filter((w) => !wallsInRoomA.has(w.uuid));

        roomAWallsArray.forEach((wA) => {
          otherWallsArray.forEach((wOther) => {
            const ov = wallsOverlap(wA, wOther);
            if (ov) {
              const lenA = wA.startPoint.distanceTo(wA.endPoint);
              const lenOther = wOther.startPoint.distanceTo(wOther.endPoint);

              if ((ov.t1End - ov.t1Start) * lenA > SLIVER_TOLERANCE) {
                if (!wallsToSplit.has(wA.uuid)) wallsToSplit.set(wA.uuid, new Set());
                if (!wallsToSplit.has(wOther.uuid)) wallsToSplit.set(wOther.uuid, new Set());

                let t1S = ov.t1Start,
                  t1E = ov.t1End;
                if (t1S * lenA < SLIVER_TOLERANCE) t1S = 0;
                if ((1 - t1E) * lenA < SLIVER_TOLERANCE) t1E = 1;

                let t2S = ov.t2Start,
                  t2E = ov.t2End;
                if (t2S * lenOther < SLIVER_TOLERANCE) t2S = 0;
                if ((1 - t2E) * lenOther < SLIVER_TOLERANCE) t2E = 1;

                wallsToSplit.get(wA.uuid)!.add(t1S);
                wallsToSplit.get(wA.uuid)!.add(t1E);
                wallsToSplit.get(wOther.uuid)!.add(t2S);
                wallsToSplit.get(wOther.uuid)!.add(t2E);
              }
            }
          });
        });

        for (const [uuid, cutSet] of wallsToSplit.entries()) {
          const safeCuts = Array.from(cutSet)
            .filter((t) => t > 1e-4 && t < 1 - 1e-4)
            .sort((a, b) => a - b);
          if (safeCuts.length > 0) {
            tempBuilding = splitWallInBuildingHelper(tempBuilding, uuid, safeCuts);
          }
        }

        const finalBuilding = consolidateBuildingWalls(tempBuilding);

        return {
          ...finalBuilding,
          isSetByUndoRedo: false,
          lastAction: EHistoryAction.ROOM_CREATED,
        };
      });
    },
    [setBuilding, updateCameraPositionSprites],
  );

  function connectTwoRooms(wallIndexA: number, doorIndexA: number, wallIndexB: number, doorIndexB: number) {
    setBuilding((prev) => {
      if (!prev || !prev.walls) return prev;
      const building = prev;
      const wallA = building.walls[wallIndexA];
      const wallB = building.walls[wallIndexB];
      if (!wallA || !wallB) return building;

      const refsA = collectBuildingEdgesForWall(building, wallA.uuid);
      const refsB = collectBuildingEdgesForWall(building, wallB.uuid);
      if (refsA.length === 0 || refsB.length === 0) return building;

      const ownerA = refsA[0].ref;
      const ownerB = refsB[0].ref;
      const roomA = building.rooms[ownerA.roomIdx];
      const roomB = building.rooms[ownerB.roomIdx];
      if (!roomA || !roomB) return building;

      const { t0: tA0, t1: tA1 } = computeDoorHostSegment(wallA, doorIndexA);
      const { t0: tB0, t1: tB1 } = computeDoorHostSegment(wallB, doorIndexB);

      let a0 = wallA.startPoint.clone().lerp(wallA.endPoint, tA0);
      let a1 = wallA.startPoint.clone().lerp(wallA.endPoint, tA1);
      const b0 = wallB.startPoint.clone().lerp(wallB.endPoint, tB0);
      const b1 = wallB.startPoint.clone().lerp(wallB.endPoint, tB1);

      const dirA = a1.clone().sub(a0).normalize();
      const dirB = b1.clone().sub(b0).normalize();

      const normalA = new THREE.Vector3(-dirA.y, dirA.x, 0).normalize();
      const normalB = new THREE.Vector3(-dirB.y, dirB.x, 0).normalize();

      if (normalA.dot(normalB) > 0) {
        const mirrorPoint = (p: THREE.Vector3) => {
          const v = p.clone().sub(a0);
          const proj = dirA.clone().multiplyScalar(v.dot(dirA));
          const perp = v.clone().sub(proj);
          return p.clone().sub(perp.multiplyScalar(2));
        };
        for (const w of roomA.spaces[0].walls) {
          w.startPoint = mirrorPoint(w.startPoint);
          w.endPoint = mirrorPoint(w.endPoint);
        }
        a0 = mirrorPoint(a0);
        a1 = mirrorPoint(a1);
      }

      const finalDirA = a1.clone().sub(a0).normalize();
      const angle = Math.atan2(dirB.y, dirB.x) - Math.atan2(finalDirA.y, finalDirA.x);
      const pivot = a0.clone();

      for (const w of roomA.spaces[0].walls) {
        const rotate = (p: THREE.Vector3) => {
          const v = p.clone().sub(pivot);
          p.set(v.x * Math.cos(angle) - v.y * Math.sin(angle) + pivot.x, v.x * Math.sin(angle) + v.y * Math.cos(angle) + pivot.y, 0);
        };
        rotate(w.startPoint);
        rotate(w.endPoint);
      }

      const midA = a0.clone().add(a1).multiplyScalar(0.5);
      const midB = b0.clone().add(b1).multiplyScalar(0.5);
      const translation = midB.clone().sub(midA);

      const movedWallsA = roomA.spaces[0].walls.map((w) => {
        const nw = new StandardWallModel(w.startPoint.clone().add(translation), w.endPoint.clone().add(translation));
        nw.depth = (w as any).depth ?? nw.depth;
        return nw;
      });

      const newWalls = [...building.walls.filter((w) => !roomA.spaces[0].walls.includes(w)), ...movedWallsA];
      const shared = new StandardWallModel(b0.clone(), b1.clone());
      shared.depth = wallB.depth;
      newWalls.push(shared);

      const newBuilding = { ...building, walls: newWalls };

      const getApproxCenter = (space: ISpace) => {
        const pts = space.walls.map((w) => w.startPoint).filter(Boolean);
        const c = new THREE.Vector3();
        pts.forEach((p) => c.add(p));
        return c.divideScalar(pts.length || 1);
      };

      const gapA = signedGapToLine(shared.startPoint, shared.endPoint, getApproxCenter(roomA.spaces[0]));
      const gapB = signedGapToLine(shared.startPoint, shared.endPoint, getApproxCenter(roomB.spaces[0]));

      const edgeA = new BoundaryEdge(shared, 0, 1, gapA > 0 ? 'A' : 'B', false);
      const edgeB = new BoundaryEdge(shared, 0, 1, gapB > 0 ? 'A' : 'B', false);

      const boundaryA = roomA.spaces[0].boundaries[ownerA.boundaryIdx] as PhysicalBoundaryModel;
      const boundaryB = roomB.spaces[0].boundaries[ownerB.boundaryIdx] as PhysicalBoundaryModel;

      boundaryA.setEdges([...boundaryA.edges.filter((e) => e.wall.uuid !== shared.uuid), edgeA]);
      boundaryB.setEdges([...boundaryB.edges.filter((e) => e.wall.uuid !== shared.uuid), edgeB]);

      return newBuilding;
    });
  }

  return { addAdjacentRoomWallsFromDoor, connectTwoRooms, connectRoomViaDoor };
};
